import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowDownUp, 
  Settings, 
  Clock, 
  AlertTriangle,
  ArrowDown,
  Loader2,
  Info,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface Token {
  symbol: string;
  name: string;
  balance: number;
  price: number;
  icon?: string;
}

interface SwapQuote {
  fromToken: string;
  toToken: string;
  fromAmount: number;
  toAmount: number;
  priceImpact: number;
  minimumReceived: number;
  fee: number;
  route: string[];
  exchangeRate: number;
}

interface SwapTransaction {
  id: string;
  fromToken: string;
  toToken: string;
  fromAmount: number;
  toAmount: number;
  priceImpact: number;
  fee: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  txHash?: string;
  createdAt: string;
}


export default function AdvancedSwap() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'swap' | 'history'>('swap');
  const [fromToken, setFromToken] = useState('');
  const [toToken, setToToken] = useState('');
  const [fromAmount, setFromAmount] = useState('');
  const [slippage, setSlippage] = useState('0.5');
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState<'from' | 'to' | null>(null);

  // Fetch available tokens
  const { data: tokens } = useQuery({
    queryKey: ['swap-tokens'],
    queryFn: async () => {
      const response = await api.get('/crypto/tokens');
      return response.data as Token[];
    },
  });

  // Fetch swap history
  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ['swap-history'],
    queryFn: async () => {
      const response = await api.get('/crypto/swap/history');
      return response.data as SwapTransaction[];
    },
    enabled: activeTab === 'history',
  });

  // Get quote mutation
  const quoteMutation = useMutation({
    mutationFn: async (data: { fromToken: string; toToken: string; amount: number }) => {
      const response = await api.post('/crypto/swap/quote', data);
      return response.data as SwapQuote;
    },
    onSuccess: (data) => {
      setQuote(data);
    },
  });

  // Execute swap mutation
  const swapMutation = useMutation({
    mutationFn: async (data: { fromToken: string; toToken: string; amount: number; slippage: number }) => {
      const response = await api.post('/crypto/swap/execute', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swap-history'] });
      queryClient.invalidateQueries({ queryKey: ['swap-tokens'] });
      setShowConfirmModal(false);
      setFromAmount('');
      setQuote(null);
    },
  });

  // Save settings mutation
  const settingsMutation = useMutation({
    mutationFn: async (data: { slippageTolerance: number }) => {
      const response = await api.put('/crypto/swap/settings', data);
      return response.data;
    },
    onSuccess: () => {
      setShowSettingsModal(false);
    },
  });

  const formatNumber = (value: number, decimals = 4) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleGetQuote = () => {
    if (fromToken && toToken && fromAmount) {
      quoteMutation.mutate({
        fromToken,
        toToken,
        amount: parseFloat(fromAmount),
      });
    }
  };

  const handleSwap = () => {
    if (quote) {
      swapMutation.mutate({
        fromToken: quote.fromToken,
        toToken: quote.toToken,
        amount: quote.fromAmount,
        slippage: parseFloat(slippage),
      });
    }
  };

  const swapTokens = () => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    setQuote(null);
  };

  const selectToken = (symbol: string) => {
    if (showTokenModal === 'from') {
      setFromToken(symbol);
    } else {
      setToToken(symbol);
    }
    setShowTokenModal(null);
    setQuote(null);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      COMPLETED: 'Concluído',
      PENDING: 'Pendente',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const getToken = (symbol: string) => tokens?.find(t => t.symbol === symbol);
  const fromTokenData = getToken(fromToken);
  const toTokenData = getToken(toToken);

  const isPriceImpactHigh = quote && quote.priceImpact > 3;

  return (
    <PageContainer
      title="Swap Avançado"
      description="Troque tokens com roteamento otimizado"
      icon={ArrowDownUp}
      actions={
        <Button variant="ghost" onClick={() => setShowSettingsModal(true)}>
          <Settings className="w-4 h-4" />
        </Button>
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'swap' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('swap')}
        >
          <ArrowDownUp className="w-4 h-4 mr-2" />
          Swap
        </Button>
        <Button
          variant={activeTab === 'history' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('history')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Histórico
        </Button>
      </div>

      {/* Swap Tab */}
      {activeTab === 'swap' && (
        <div className="max-w-xl mx-auto space-y-4">
          <Card>
            <div className="p-6 space-y-4">
              {/* From Token */}
              <div className="p-4 bg-surface-800 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-surface-400">De</span>
                  {fromTokenData && (
                    <span className="text-sm text-surface-400">
                      Saldo: {formatNumber(fromTokenData.balance)}
                    </span>
                  )}
                </div>
                <div className="flex gap-4">
                  <Input
                    type="number"
                    placeholder="0.0"
                    value={fromAmount}
                    onChange={(e) => {
                      setFromAmount(e.target.value);
                      setQuote(null);
                    }}
                    className="flex-1 text-xl"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => setShowTokenModal('from')}
                    className="min-w-[120px]"
                  >
                    {fromToken || 'Selecionar'}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                {fromTokenData && fromAmount && (
                  <p className="text-sm text-surface-400 mt-2">
                    ≈ {formatCurrency(parseFloat(fromAmount) * fromTokenData.price)}
                  </p>
                )}
              </div>

              {/* Swap Button */}
              <div className="flex justify-center -my-2 relative z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full bg-surface-800 border border-surface-700"
                  onClick={swapTokens}
                >
                  <ArrowDown className="w-5 h-5" />
                </Button>
              </div>

              {/* To Token */}
              <div className="p-4 bg-surface-800 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-surface-400">Para</span>
                  {toTokenData && (
                    <span className="text-sm text-surface-400">
                      Saldo: {formatNumber(toTokenData.balance)}
                    </span>
                  )}
                </div>
                <div className="flex gap-4">
                  <Input
                    type="number"
                    placeholder="0.0"
                    value={quote ? formatNumber(quote.toAmount) : ''}
                    readOnly
                    className="flex-1 text-xl"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => setShowTokenModal('to')}
                    className="min-w-[120px]"
                  >
                    {toToken || 'Selecionar'}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                {quote && toTokenData && (
                  <p className="text-sm text-surface-400 mt-2">
                    ≈ {formatCurrency(quote.toAmount * toTokenData.price)}
                  </p>
                )}
              </div>

              {/* Quote Details */}
              {quote && (
                <div className="p-4 bg-surface-800/50 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-surface-400">Taxa de câmbio</span>
                    <span className="text-surface-100">
                      1 {quote.fromToken} = {formatNumber(quote.exchangeRate)} {quote.toToken}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Impacto no preço</span>
                    <span className={isPriceImpactHigh ? 'text-error' : 'text-surface-100'}>
                      {quote.priceImpact.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Mínimo recebido</span>
                    <span className="text-surface-100">
                      {formatNumber(quote.minimumReceived)} {quote.toToken}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Taxa</span>
                    <span className="text-surface-100">
                      {formatNumber(quote.fee)} {quote.fromToken}
                    </span>
                  </div>
                  {quote.route.length > 2 && (
                    <div className="flex justify-between">
                      <span className="text-surface-400">Rota</span>
                      <span className="text-surface-100">
                        {quote.route.join(' → ')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Price Impact Warning */}
              {isPriceImpactHigh && (
                <div className="flex items-start gap-2 p-3 bg-error/10 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error">
                    Impacto no preço alto ({quote?.priceImpact.toFixed(2)}%). 
                    Considere reduzir o valor ou dividir em múltiplas transações.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {!quote ? (
                <Button
                  className="w-full"
                  onClick={handleGetQuote}
                  disabled={!fromToken || !toToken || !fromAmount || quoteMutation.isPending}
                >
                  {quoteMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Calculando...
                    </>
                  ) : (
                    'Obter Cotação'
                  )}
                </Button>
              ) : (
                <div className="flex gap-4">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setQuote(null);
                      handleGetQuote();
                    }}
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Atualizar
                  </Button>
                  <Button
                    onClick={() => setShowConfirmModal(true)}
                    className="flex-1"
                  >
                    Trocar
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Slippage Info */}
          <Card>
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-surface-400" />
                <span className="text-sm text-surface-400">Tolerância de slippage</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowSettingsModal(true)}>
                {slippage}%
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Histórico de Swaps</h3>
            {loadingHistory ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((tx) => (
                  <div key={tx.id} className="p-4 bg-surface-800 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-surface-100 font-medium">
                          {formatNumber(tx.fromAmount)} {tx.fromToken}
                        </span>
                        <ArrowDown className="w-4 h-4 text-surface-400 rotate-[-90deg]" />
                        <span className="text-surface-100 font-medium">
                          {formatNumber(tx.toAmount)} {tx.toToken}
                        </span>
                      </div>
                      {getStatusBadge(tx.status)}
                    </div>
                    <div className="flex justify-between text-sm text-surface-400">
                      <span>Impacto: {tx.priceImpact.toFixed(2)}%</span>
                      <span>{formatDate(tx.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhum swap encontrado
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Token Selection Modal */}
      <Modal
        isOpen={!!showTokenModal}
        onClose={() => setShowTokenModal(null)}
        title={`Selecionar Token (${showTokenModal === 'from' ? 'De' : 'Para'})`}
      >
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {tokens?.map((token) => (
            <button
              key={token.symbol}
              onClick={() => selectToken(token.symbol)}
              disabled={
                (showTokenModal === 'from' && token.symbol === toToken) ||
                (showTokenModal === 'to' && token.symbol === fromToken)
              }
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-surface-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-sm font-bold text-primary-400">
                  {token.symbol.slice(0, 2)}
                </div>
                <div className="text-left">
                  <p className="text-surface-100 font-medium">{token.symbol}</p>
                  <p className="text-sm text-surface-400">{token.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-surface-100">{formatNumber(token.balance)}</p>
                <p className="text-sm text-surface-400">{formatCurrency(token.balance * token.price)}</p>
              </div>
            </button>
          ))}
        </div>
      </Modal>

      {/* Settings Modal */}
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title="Configurações de Swap"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-surface-400 mb-2">Tolerância de Slippage</label>
            <div className="flex gap-2">
              {['0.1', '0.5', '1.0'].map((value) => (
                <Button
                  key={value}
                  variant={slippage === value ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setSlippage(value)}
                >
                  {value}%
                </Button>
              ))}
              <Input
                type="number"
                value={slippage}
                onChange={(e) => setSlippage(e.target.value)}
                className="w-20"
                step="0.1"
                min="0.1"
                max="50"
              />
            </div>
            <p className="text-xs text-surface-400 mt-2">
              Sua transação será revertida se o preço mudar mais que este percentual.
            </p>
          </div>
          <Button
            className="w-full"
            onClick={() => settingsMutation.mutate({ slippageTolerance: parseFloat(slippage) })}
          >
            Salvar
          </Button>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Swap"
      >
        {quote && (
          <div className="space-y-4">
            <div className="p-4 bg-surface-800 rounded-lg space-y-3">
              <div className="text-center">
                <p className="text-surface-400 text-sm">Você envia</p>
                <p className="text-2xl font-bold text-surface-100">
                  {formatNumber(quote.fromAmount)} {quote.fromToken}
                </p>
              </div>
              <div className="flex justify-center">
                <ArrowDown className="w-6 h-6 text-primary-400" />
              </div>
              <div className="text-center">
                <p className="text-surface-400 text-sm">Você recebe</p>
                <p className="text-2xl font-bold text-success">
                  {formatNumber(quote.toAmount)} {quote.toToken}
                </p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Mínimo recebido</span>
                <span className="text-surface-100">{formatNumber(quote.minimumReceived)} {quote.toToken}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Impacto no preço</span>
                <span className={isPriceImpactHigh ? 'text-error' : 'text-surface-100'}>
                  {quote.priceImpact.toFixed(2)}%
                </span>
              </div>
            </div>

            {isPriceImpactHigh && (
              <div className="flex items-start gap-2 p-3 bg-error/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-error flex-shrink-0" />
                <p className="text-sm text-error">
                  Impacto no preço muito alto. Prossiga com cautela.
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSwap}
                disabled={swapMutation.isPending}
                className="flex-1"
              >
                {swapMutation.isPending ? 'Processando...' : 'Confirmar Swap'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
